import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_FAVORITE_PROFESSIONAL } from '~/actions';
import { clearSuccess } from '~/actions/success';
import { createSuccessSelector } from '~/api/selectors';

const successAddToFavoritesSelector = createSuccessSelector([ADD_FAVORITE_PROFESSIONAL]);

/**
 * Whether to show the "Added to Favorites" confirmation, driven by the redux
 * success flag for `ADD_FAVORITE_PROFESSIONAL`.
 *
 * Keyed off the flag rather than the click so the confirmation only appears once
 * the request actually succeeded — a favourite that failed must not be
 * celebrated. The same signal and the same shape the Professionals Hub uses, so
 * the two surfaces cannot drift on when the popup is earned.
 *
 * **The flag is global and outlives the screen that set it**, which is the trap
 * here: favourite somebody in the Hub, navigate to a completed shift, and a
 * stale `true` would greet the office with a congratulation for a professional
 * they are not looking at. So the first effect run clears the flag instead of
 * reading it, and every later run clears it again on the way to opening the
 * popup, leaving nothing behind for the next screen.
 */
export function useFavoriteAddedPopup(): { isOpen: boolean; close: () => void } {
  const dispatch = useDispatch();
  const isAddedToFavoritesSuccess = Boolean(
    useSelector((state) => successAddToFavoritesSelector(state)),
  );

  const [isOpen, setIsOpen] = useState(false);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      dispatch(clearSuccess(ADD_FAVORITE_PROFESSIONAL));

      return;
    }

    if (isAddedToFavoritesSuccess) {
      setIsOpen(true);
      dispatch(clearSuccess(ADD_FAVORITE_PROFESSIONAL));
    }
  }, [isAddedToFavoritesSuccess, dispatch]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, close };
}
